import { YesNoAnswer } from 'scenes/common/types';
import { defaultPaymentDetails } from 'scenes/common/flows/paymentDetails/defaults';
import {
  getPaymentDate,
  getPolicyStartDate,
  getPreselectedPaymentFrequency,
  getPropertyBuiltYear,
  hasAdditionalPolicyHolders,
  hasBuildingCoverType,
  hasContentsCoverType,
  heritageListedAnswerRequired,
  isPolicyIssuedToPerson,
  isPostalAddressDifferentManual,
  isPostalAddressLockedBagGpo,
  isPropertyUsedForBusiness,
  rewiredOrReplumbedAnswerRequired,
  sameRiskAndPostalAddress,
} from 'scenes/home/selectors';
import {
  HomeFlowDefinition,
  HomeFlowType,
  HomeFormAnswer,
  HomeFunnelState,
  HomeStepType,
} from 'scenes/home/types';
import { defaultPolicyHolder } from 'scenes/home/flows/policyDetails/defaults';

export const forwardLinks: HomeFlowDefinition = {
  [HomeStepType.PropertyCondition]: (funnelState: HomeFunnelState) => {
    if (heritageListedAnswerRequired(getPropertyBuiltYear(funnelState))) {
      return { step: HomeStepType.HeritageListed };
    }
    return {
      step: rewiredOrReplumbedAnswerRequired(getPropertyBuiltYear(funnelState))
        ? HomeStepType.RewiredOrReplumbed
        : HomeStepType.PropertyUnoccupied,
    };
  },
  [HomeStepType.HeritageListed]: (funnelState: HomeFunnelState) => ({
    step: rewiredOrReplumbedAnswerRequired(getPropertyBuiltYear(funnelState))
      ? HomeStepType.RewiredOrReplumbed
      : HomeStepType.PropertyUnoccupied,
  }),
  [HomeStepType.RewiredOrReplumbed]: { step: HomeStepType.PropertyUnoccupied },
  [HomeStepType.PropertyUnoccupied]: { step: HomeStepType.PropertyRenovation },
  [HomeStepType.PropertyRenovation]: { step: HomeStepType.Over5Acres },
  [HomeStepType.Over5Acres]: { step: HomeStepType.PropertyOccupancy },
  [HomeStepType.PropertyOccupancy]: (funnelState: HomeFunnelState) => ({
    step: isPropertyUsedForBusiness(funnelState)
      ? HomeStepType.CashKeptAtHome
      : HomeStepType.OccupantsHistory,
  }),
  [HomeStepType.CashKeptAtHome]: { step: HomeStepType.StockKeptAtHome },
  [HomeStepType.StockKeptAtHome]: { step: HomeStepType.HazardGoodsKeptAtHome },
  [HomeStepType.HazardGoodsKeptAtHome]: { step: HomeStepType.NumberOfVisits },
  [HomeStepType.NumberOfVisits]: {
    step: HomeStepType.BusinessRegisteredForGst,
  },
  [HomeStepType.BusinessRegisteredForGst]: {
    step: HomeStepType.OccupantsHistory,
  },
  [HomeStepType.OccupantsHistory]: (funnelState: HomeFunnelState) => {
    if (hasBuildingCoverType(funnelState)) {
      return { step: HomeStepType.MortgageProvider };
    }
    return {
      step: isPolicyIssuedToPerson(funnelState)
        ? HomeStepType.PersonalPolicyContact
        : HomeStepType.BusinessNameAndAbn,
    };
  },
  [HomeStepType.MortgageProvider]: (funnelState: HomeFunnelState) => ({
    step: isPolicyIssuedToPerson(funnelState)
      ? HomeStepType.PersonalPolicyContact
      : HomeStepType.BusinessNameAndAbn,
  }),

  [HomeStepType.BusinessNameAndAbn]: { step: HomeStepType.BusinessContact },
  [HomeStepType.BusinessContact]: { step: HomeStepType.ContactEmailAddress },
  [HomeStepType.PersonalPolicyContact]: {
    step: HomeStepType.ContactEmailAddress,
  },
  [HomeStepType.ContactEmailAddress]: { step: HomeStepType.ContactPhoneNumber },
  [HomeStepType.ContactPhoneNumber]: (funnelState: HomeFunnelState) => ({
    step: hasAdditionalPolicyHolders(funnelState)
      ? HomeStepType.AdditionalPolicyHolderList
      : HomeStepType.AddAdditionalPolicyHolder,
  }),
  [HomeStepType.AddAdditionalPolicyHolder]: {
    step: HomeStepType.PostalAddress,
  },
  [HomeStepType.AddAdditionalPolicyHolder]: (
    funnelState: HomeFunnelState,
    answer: HomeFormAnswer
  ) =>
    answer.formValues.addAdditionalPolicyHolder === YesNoAnswer.Yes
      ? {
          step: HomeStepType.AdditionalPolicyHolder,
          policyDetails: {
            ...funnelState.policyDetails,
            additionalPolicyHolder: defaultPolicyHolder,
          },
        }
      : {
          step: HomeStepType.PostalAddress,
        },
  [HomeStepType.AddAdditionalPolicyHolder]: (
    funnelState: HomeFunnelState,
    answer: HomeFormAnswer
  ) =>
    answer.formValues.addAdditionalPolicyHolder === YesNoAnswer.Yes
      ? {
          step: HomeStepType.AdditionalPolicyHolder,
          policyDetails: {
            ...funnelState.policyDetails,
            additionalPolicyHolder: defaultPolicyHolder,
          },
        }
      : {
          step: HomeStepType.PostalAddress,
        },
  [HomeStepType.AdditionalPolicyHolder]: {
    step: HomeStepType.AdditionalPolicyHolderList,
  },
  [HomeStepType.AdditionalPolicyHolderList]: (
    funnelState: HomeFunnelState,
    answer: HomeFormAnswer
  ) =>
    answer.formValues.addAdditionalPolicyHolder === YesNoAnswer.Yes
      ? {
          step: HomeStepType.AdditionalPolicyHolder,
          policyDetails: {
            ...funnelState.policyDetails,
            additionalPolicyHolder:
              answer.formValues.additionalPolicyHolderIndex !== undefined
                ? {
                    ...funnelState.policyDetails.additionalPolicyHolders[
                      answer.formValues.additionalPolicyHolderIndex
                    ],
                    index: answer.formValues.additionalPolicyHolderIndex,
                  }
                : defaultPolicyHolder,
          },
        }
      : {
          step: HomeStepType.PostalAddress,
        },

  [HomeStepType.PostalAddress]: (funnelState: HomeFunnelState) => {
    if (sameRiskAndPostalAddress(funnelState)) {
      return { step: HomeStepType.TermsAndConditions };
    }
    return {
      step: isPostalAddressLockedBagGpo(funnelState)
        ? HomeStepType.LockedBagAddress
        : HomeStepType.DifferentPostalAddress,
    };
  },
  [HomeStepType.DifferentPostalAddress]: (funnelState: HomeFunnelState) => ({
    step: isPostalAddressDifferentManual(funnelState)
      ? HomeStepType.DifferentPostalAddressManual
      : HomeStepType.TermsAndConditions,
  }),
  [HomeStepType.DifferentPostalAddressManual]: {
    step: HomeStepType.TermsAndConditions,
  },
  [HomeStepType.LockedBagAddress]: { step: HomeStepType.TermsAndConditions },
  // [HomeStepType.MarketingConsent]: { step: HomeStepType.TermsAndConditions },
  [HomeStepType.TermsAndConditions]: { step: HomeStepType.Summary },
  [HomeStepType.Summary]: (funnelState: HomeFunnelState) => ({
    step: HomeStepType.PaymentFrequency,
    flow: HomeFlowType.PaymentDetails,
    [HomeFlowType.PaymentDetails]: {
      ...defaultPaymentDetails,
      paymentFrequency: getPreselectedPaymentFrequency(funnelState),
      paymentDate:
        getPaymentDate(funnelState) || getPolicyStartDate(funnelState),
    },
  }),
};

export const backwardLinks: HomeFlowDefinition = {
  [HomeStepType.PropertyCondition]: (funnelState: HomeFunnelState) => ({
    step: hasContentsCoverType(funnelState)
      ? HomeStepType.UnspecifiedPortableContents
      : HomeStepType.OptionalExtras,
    flow: HomeFlowType.CoverOptions,
    [HomeFlowType.CoverOptions]: {
      ...funnelState.coverOptions,
      preselectedPaymentFrequency: getPreselectedPaymentFrequency(funnelState),
    },
  }),
  [HomeStepType.HeritageListed]: { step: HomeStepType.PropertyCondition },
  [HomeStepType.RewiredOrReplumbed]: (funnelState: HomeFunnelState) => ({
    step: heritageListedAnswerRequired(getPropertyBuiltYear(funnelState))
      ? HomeStepType.HeritageListed
      : HomeStepType.PropertyCondition,
  }),
  [HomeStepType.PropertyUnoccupied]: (funnelState: HomeFunnelState) => {
    if (rewiredOrReplumbedAnswerRequired(getPropertyBuiltYear(funnelState))) {
      return { step: HomeStepType.RewiredOrReplumbed };
    }
    return {
      step: heritageListedAnswerRequired(getPropertyBuiltYear(funnelState))
        ? HomeStepType.HeritageListed
        : HomeStepType.PropertyCondition,
    };
  },
  [HomeStepType.PropertyRenovation]: { step: HomeStepType.PropertyUnoccupied },
  [HomeStepType.Over5Acres]: { step: HomeStepType.PropertyRenovation },
  [HomeStepType.PropertyOccupancy]: { step: HomeStepType.Over5Acres },
  [HomeStepType.CashKeptAtHome]: { step: HomeStepType.PropertyOccupancy },
  [HomeStepType.StockKeptAtHome]: { step: HomeStepType.CashKeptAtHome },
  [HomeStepType.HazardGoodsKeptAtHome]: { step: HomeStepType.StockKeptAtHome },
  [HomeStepType.NumberOfVisits]: { step: HomeStepType.HazardGoodsKeptAtHome },
  [HomeStepType.BusinessRegisteredForGst]: {
    step: HomeStepType.NumberOfVisits,
  },
  [HomeStepType.OccupantsHistory]: (funnelState: HomeFunnelState) => ({
    step: isPropertyUsedForBusiness(funnelState)
      ? HomeStepType.BusinessRegisteredForGst
      : HomeStepType.PropertyOccupancy,
  }),
  [HomeStepType.MortgageProvider]: { step: HomeStepType.OccupantsHistory },
  [HomeStepType.BusinessNameAndAbn]: (funnelState: HomeFunnelState) => ({
    step: hasBuildingCoverType(funnelState)
      ? HomeStepType.MortgageProvider
      : HomeStepType.OccupantsHistory,
  }),
  [HomeStepType.BusinessContact]: { step: HomeStepType.BusinessNameAndAbn },
  [HomeStepType.PersonalPolicyContact]: (funnelState: HomeFunnelState) => ({
    step: hasBuildingCoverType(funnelState)
      ? HomeStepType.MortgageProvider
      : HomeStepType.OccupantsHistory,
  }),
  [HomeStepType.ContactEmailAddress]: (funnelState: HomeFunnelState) => ({
    step: isPolicyIssuedToPerson(funnelState)
      ? HomeStepType.PersonalPolicyContact
      : HomeStepType.BusinessContact,
  }),
  [HomeStepType.ContactPhoneNumber]: { step: HomeStepType.ContactEmailAddress },
  [HomeStepType.AddAdditionalPolicyHolder]: {
    step: HomeStepType.ContactPhoneNumber,
  },
  [HomeStepType.AdditionalPolicyHolder]: (funnelState: HomeFunnelState) => ({
    step: hasAdditionalPolicyHolders(funnelState)
      ? HomeStepType.AdditionalPolicyHolderList
      : HomeStepType.AddAdditionalPolicyHolder,
  }),
  [HomeStepType.AdditionalPolicyHolderList]: {
    step: HomeStepType.ContactPhoneNumber,
  },
  [HomeStepType.PostalAddress]: (funnelState: HomeFunnelState) => ({
    step: hasAdditionalPolicyHolders(funnelState)
      ? HomeStepType.AdditionalPolicyHolderList
      : HomeStepType.AddAdditionalPolicyHolder,
  }),

  [HomeStepType.DifferentPostalAddress]: { step: HomeStepType.PostalAddress },
  [HomeStepType.DifferentPostalAddressManual]: {
    step: HomeStepType.DifferentPostalAddress,
  },
  [HomeStepType.LockedBagAddress]: { step: HomeStepType.PostalAddress },
  [HomeStepType.TermsAndConditions]: (funnelState: HomeFunnelState) => {
    if (sameRiskAndPostalAddress(funnelState)) {
      return { step: HomeStepType.PostalAddress };
    }
    if (isPostalAddressLockedBagGpo(funnelState)) {
      return { step: HomeStepType.LockedBagAddress };
    }
    if (isPostalAddressDifferentManual(funnelState)) {
      return { step: HomeStepType.DifferentPostalAddressManual };
    }
    return { step: HomeStepType.DifferentPostalAddress };
  },

  [HomeStepType.TermsAndConditions]: { step: HomeStepType.PostalAddress },
  // [HomeStepType.TermsAndConditions]: { step: HomeStepType.MarketingConsent },
  [HomeStepType.Summary]: { step: HomeStepType.TermsAndConditions },
};

export const editPolicyDetails = (
  funnelState: HomeFunnelState,
  action: HomeFormAnswer
) => ({
  step: action.targetStep,
  flow: HomeFlowType.PolicyDetails,
});
